import { useState } from 'react';
import { Clock, Loader2, Mail, MapPin, Phone, Send } from 'lucide-react';
import PageHero from '../components/sections/PageHero';
import FaqSection from '../components/sections/FaqSection';
import Container from '../components/ui/Container';
import Reveal from '../components/ui/Reveal';
import Button from '../components/ui/Button';
import { Input, Textarea } from '../components/ui/Field';
import { useToast } from '../components/ui/Toast';
import { site } from '../data/site';
import { email as emailRule, minLength, phone as phoneRule, required, validate } from '../lib/validators';

const EMPTY = { name: '', email: '', phone: '', company: '', message: '' };

const SCHEMA = {
  name: [required('Name'), minLength('Name', 2)],
  email: [required('Email'), emailRule],
  phone: [phoneRule],
  message: [required('Message'), minLength('Message', 15)],
};

export default function Contact() {
  const toast = useToast();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [sending, setSending] = useState(false);

  const setField = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (touched[field]) {
      const next = validate({ ...values, [field]: value }, { [field]: SCHEMA[field] || [] });
      setErrors((prev) => ({ ...prev, [field]: next[field] || '' }));
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const next = validate(values, { [field]: SCHEMA[field] || [] });
    setErrors((prev) => ({ ...prev, [field]: next[field] || '' }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate(values, SCHEMA);
    setErrors(nextErrors);
    setTouched(Object.keys(SCHEMA).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

    if (Object.keys(nextErrors).length > 0) {
      toast({
        variant: 'error',
        title: 'Please check the highlighted fields',
        description: 'A few details are missing or invalid.',
      });
      return;
    }

    setSending(true);
    setTimeout(() => {
      setSending(false);
      setValues(EMPTY);
      setTouched({});
      toast({
        title: 'Message sent',
        description: 'Our compliance desk responds within four working hours.',
      });
    }, 700);
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Talk to the team that will handle your file"
        description="Enquiries reach a compliance manager, not a queue. We respond within four working hours on business days."
      />

      <section className="section bg-surface">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr] lg:gap-10">
            {/* Contact details */}
            <Reveal direction="right" className="space-y-5">
              <div className="rounded-xl2 border border-ink-100 bg-white p-7 shadow-soft">
                <h2 className="font-display text-lg font-semibold text-ink-900">Reach us directly</h2>

                <ul className="mt-6 space-y-5 text-sm">
                  <li className="flex gap-4">
                    <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-brand-50 text-brand-700">
                      <MapPin className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-semibold text-ink-900">Facility & registered office</p>
                      <address className="mt-1 not-italic leading-relaxed text-ink-500">
                        {site.addressLines.map((line) => (
                          <span key={line} className="block">
                            {line}
                          </span>
                        ))}
                      </address>
                    </div>
                  </li>

                  <li className="flex gap-4">
                    <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-brand-50 text-brand-700">
                      <Phone className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-semibold text-ink-900">Phone</p>
                      <a
                        href={`tel:${site.phone.replace(/\s/g, '')}`}
                        className="mt-1 block text-ink-500 transition hover:text-brand-700"
                      >
                        {site.phone}
                      </a>
                    </div>
                  </li>

                  <li className="flex gap-4">
                    <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-brand-50 text-brand-700">
                      <Mail className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-ink-900">Email</p>
                      <a
                        href={`mailto:${site.email}`}
                        className="mt-1 block break-words text-ink-500 transition hover:text-brand-700"
                      >
                        {site.email}
                      </a>
                    </div>
                  </li>

                  <li className="flex gap-4">
                    <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-brand-50 text-brand-700">
                      <Clock className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="font-semibold text-ink-900">Working hours</p>
                      <ul className="mt-1 space-y-0.5 text-ink-500">
                        {site.hours.map((entry) => (
                          <li key={entry.day}>
                            {entry.day} · {entry.time}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                </ul>

                
              </div>

              {/* Stylised location card — no external map embed */}
              <MapCard />
            </Reveal>

            {/* Enquiry form */}
            <Reveal direction="left" delay={0.1}>
              <form
                onSubmit={handleSubmit}
                noValidate
                className="rounded-xl2 border border-ink-100 bg-white p-7 shadow-soft sm:p-8"
              >
                <h2 className="font-display text-lg font-semibold text-ink-900">Send us a message</h2>
                <p className="mt-2 text-sm text-ink-500">
                  For a pickup quotation, the{' '}
                  <a href="/request-pickup" className="font-medium text-brand-700 underline underline-offset-2">
                    pickup request form
                  </a>{' '}
                  gets you a faster, more accurate response.
                </p>

                <div className="mt-7 space-y-6">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Input
                      label="Your Name"
                      required
                      placeholder="Full name"
                      value={values.name}
                      onChange={(e) => setField('name', e.target.value)}
                      onBlur={() => handleBlur('name')}
                      error={touched.name ? errors.name : ''}
                    />
                    <Input
                      label="Company"
                      placeholder="Organisation name"
                      value={values.company}
                      onChange={(e) => setField('company', e.target.value)}
                    />
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <Input
                      label="Work Email"
                      type="email"
                      required
                      placeholder="name@company.com"
                      value={values.email}
                      onChange={(e) => setField('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      error={touched.email ? errors.email : ''}
                    />
                    <Input
                      label="Phone"
                      type="tel"
                      placeholder="+91 98200 00000"
                      value={values.phone}
                      onChange={(e) => setField('phone', e.target.value)}
                      onBlur={() => handleBlur('phone')}
                      error={touched.phone ? errors.phone : ''}
                    />
                  </div>

                  <Textarea
                    label="How can we help?"
                    required
                    rows={6}
                    placeholder="Tell us about your requirement — waste types, locations, timelines or compliance questions."
                    value={values.message}
                    onChange={(e) => setField('message', e.target.value)}
                    onBlur={() => handleBlur('message')}
                    error={touched.message ? errors.message : ''}
                  />
                </div>

                <div className="mt-7 flex flex-col gap-4 border-t border-ink-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
                  
                  <Button type="submit" size="lg" disabled={sending} className="flex-none">
                    {sending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Reveal>
          </div>
        </Container>
      </section>

      <FaqSection limit={6} showFilters={false} />
    </>
  );
}

/** Decorative location card standing in for a map embed. */
function MapCard() {
  
  
}
